<template>
    <div class="library-select-widget">
        <div class="widget-header">
            <div class="title-container">
                <h3>Choose from Library</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <cards-list-widget
                :multiple="multipleFiles"
                :items="libraryItems"
                :vertical="true"
                :loading="isLoading"
                :tooltips-text="tooltipsText"
                @search="handleUpdateSearch"
                @show-preview="handleShowPreview"
                @input="handleInputSelect"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget" :data-locator="getDataLocator('cancel')"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button :disabled="isButtonDisabled" @click="handleNext" :data-locator="getDataLocator('next')"> Next </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import CollateralPreviewPopup from '@/components/popups/collateral-preview-popup';
import EventBus from '@/scripts/event-bus.js';
import { QaMixin } from '@/mixins/qa-mixin';

const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const CardsListWidget = () => import('@/components/sliding-widgets/cards-list-widget');

export default {
    name: 'library-select-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'cards-list-widget': CardsListWidget,
    },

    mixins: [QaMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data: () => {
        return {
            searchValue: '',
            libraryItems: [],
            selectedItems: [],
            isLoading: false,
            totalItems: 0,
            tooltipsText: {
                close: 'Deselect',
                preview: 'Preview',
            }
        };
    },

    computed: {
        fileTypes() {
            return this.widget.options.fileTypes;
        },

        clientId() {
            return this.widget.options.clientId;
        },

        agencyId() {
            return this.widget.options.agencyId;
        },

        isButtonDisabled() {
            return this.selectedItems.length === 0;
        },

        multipleFiles() {
            return this.widget.options.hasOwnProperty('multipleFiles')
                ? this.widget.options.multipleFiles
                : true;
        }
    },

    watch: {
        searchValue: {
            deep: true,
            handler() {
                this.getCollateral();
            },
        },
    },

    mounted() {
        this.getCollateral();
    },

    methods: {
        getFilterParam() {
            const queryFilters = [];

            if (this.fileTypes) {
                const fileTypeQuery = this.getFileTypeQueryParams(this.fileTypes);

                if (fileTypeQuery) {
                    queryFilters.push(fileTypeQuery);
                }
            }

            if (this.searchValue) {
                queryFilters.push(`(contains(Name, '${this.searchValue}'))`);
            }

            if (this.clientId) {
                queryFilters.push(`ClientId eq ${this.clientId}`);
            }
            else if (this.agencyId) {
                queryFilters.push(`AgencyId eq ${this.agencyId}`);
            }
            else {
                queryFilters.push(`AgencyId eq null`);
                queryFilters.push(`ClientId eq null`);
            }

            return `$filter=${queryFilters.join(' and ')}`;
        },

        getFileTypeQueryParams() {
            const typeQueryFilter = [];
            if (this.fileTypes.length > 0) {
                this.fileTypes.forEach((type) => {
                    typeQueryFilter.push(`FileType eq '${type}'`);
                });
            }

            return `(${typeQueryFilter.join(' or ')})`;
        },

        asLibraryItem(item) {
            return {
                id: item.Id,
                previewName: item.Name,
                previewImage: item.PreviewImageUrl,
                videoUrl: item.FileType === 'Video' ? item.Url : null,
                type: item.MimeType,
                fileId: item.Url.slice(item.Url.lastIndexOf('/') + 1),
                url: item.Url,
                fileSize: item.FileSizeInBytes,
                size: item.FileSizeInBytes,
            };
        },

        getCollateral() {
            this.isLoading = true;

            const filter = `${this.getFilterParam()}`;
            const count = '$count=true';
            const query = `/service/Collaterals?${filter}&${count}`;

            this.$api
                .get(query)
                .then((response) => {
                    this.totalItems = response.data['@odata.count'];
                    this.libraryItems = response.data.value.map(this.asLibraryItem);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching your collaterals.', { error });
                    this.closeWidget();
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        handleUpdateSearch(search) {
            const encodedSearch = encodeURIComponent(search);

            this.searchValue = encodedSearch;
        },

        handleInputSelect(items) {
            this.selectedItems = items;
        },

        handleShowPreview(item) {
            this.$modal.show(
                CollateralPreviewPopup,
                {
                    collateralId: item.id,
                    actions: {},
                },
                {
                    classes: 'lightbox-popup',
                    height: '100%',
                    width: '803px',
                }
            );
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleNext() {
            if (this.selectedItems.length > 0) {
                EventBus.$emit('add-library', this.selectedItems);
            }

            this.closeWidget();
        },
    },
};
</script>

<style>
.library-select-widget .content-wrapper {
    padding: 20px;
}

.library-select-widget .transparent-btn {
    background: transparent;
    color: #877d88;
}

.library-select-widget .transparent-btn:hover {
    background: #e1e0e5;
}
</style>
